import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAuthorization } from "../../hooks";
import { useState } from "react";
import { ConfirmationModal } from "../../components";
import { startImageGenerationProcess } from "../../services/imageGeneration/startImageGenerationProcess";
import { defaultPromptWithReferencePair, defaultPromptNoReference } from "../../types/imageGeneration";
import { IMatchModel } from "../../types/pinecone";
import { toast } from "react-toastify";
import { ToastError } from "../../utils/toastUtil";
import { useEffect } from "react";
import { Storage } from "aws-amplify";
import aws_config from "../../modified_aws-export";

interface IStartImageGenerationProps {
    imageId: string;
    pineconeMatches: IMatchModel[];
}

const NO_REFERENCE = "__NO_REFERENCE__";

const StartImageGeneration: React.FC<IStartImageGenerationProps> = ({imageId, pineconeMatches}) => {
    const {t} = useTranslation();
    const {hasImageGenerationPermission} = useAuthorization();
    const [isShowConfirmationModal, setIsShowConfirmationModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const availableModels = [
        {
            value: "gpt-image-1",
            label: "GPT Image 1 (Azure)",
            cost: "~$0.04–$0.17 / image",
            speed: "Medium",
            quality: "⭐⭐",
            description: "OpenAI flagship image model via Azure. Highest instruction-following accuracy and photorealistic output. Best choice for complex prompts requiring precise edits.",
        },
        {
            value: "gpt-image-1.5",
            label: "GPT Image 1.5 (Azure)",
            cost: "~$0.02–$0.10 / image",
            speed: "Medium-Fast",
            quality: "⭐⭐⭐",
            description: "Updated GPT image model on the same Azure resource. Improved efficiency over GPT Image 1 with comparable quality. Good balance of speed and accuracy.",
        },
        {
            value: "gpt-image-2",
            label: "GPT Image 2 (Azure AI Foundry)",
            cost: "~$0.02–$0.12 / image",
            speed: "Medium-Fast",
            quality: "⭐⭐⭐⭐",
            description: "OpenAI's latest image generation model via Azure AI Foundry. Improved instruction-following and image quality over GPT Image 1. Recommended for high-quality results with the newest generation.",
        },
        {
            value: "gemini-3-pro-image-preview",
            label: "Gemini 3 Pro Image (Preview)",
            cost: "~$0.03–$0.05 / image",
            speed: "Medium",
            quality: "⭐⭐⭐⭐⭐",
            description: "Google's most capable image generation model. Excellent quality and style transfer, comparable to GPT Image 1. Recommended for highest-quality Gemini output.",
        },
        {
            value: "gemini-3.1-flash-image-preview",
            label: "Gemini 3.1 Flash Image (Preview)",
            cost: "~$0.005–$0.01 / image",
            speed: "Fast",
            quality: "⭐⭐⭐⭐",
            description: "Balanced model with strong quality at a fraction of the Pro cost. Good for iterative testing and high-volume generation. Recommended for most use cases.",
        },
        {
            value: "gemini-2.5-flash-image",
            label: "Gemini 2.5 Flash Image",
            cost: "~$0.001–$0.003 / image",
            speed: "Very Fast",
            quality: "⭐⭐⭐",
            description: "Fastest and cheapest option. Suitable for quick previews and bulk processing where cost matters more than peak quality.",
        },
    ];
    const [imageGenModel, setImageGenModel] = useState<string>(availableModels[0].value);
    const [availableOemTaPairs, setAvailableOemTaPairs] = useState<any>([]);
    const [prompt, setPrompt] = useState<string>(defaultPromptNoReference);
    const [oemTaPair, setOemTaPair] = useState<string>(availableOemTaPairs[0]);
 
    const findAllOemTaPairsAsReference = () => {
        const pairs = pineconeMatches
            .filter((item: IMatchModel) => item.id.endsWith("-OEM"))
            .map((item: IMatchModel) => item.id.replace('.ai-OEM', ''))
            .slice(0, 10);
        if (pairs.length > 0) {
            pairs.push(NO_REFERENCE);
        }
        return pairs;
    };
 
    useEffect(() => {
        if (pineconeMatches.every((item: IMatchModel) => item.id.endsWith("-Single"))  ) {
            setPrompt(defaultPromptNoReference);
            setAvailableOemTaPairs([])
        } else {
            const availablePairs = findAllOemTaPairsAsReference()
            setAvailableOemTaPairs(availablePairs);
            setOemTaPair(availablePairs[0])
            setPrompt(defaultPromptWithReferencePair);
        }
    }, [pineconeMatches])
 
    const handleOemTaPairChange = (selectedPair: string) => {
        setOemTaPair(selectedPair);
        if (selectedPair === NO_REFERENCE) {
            setPrompt(defaultPromptNoReference);
        } else {
            setPrompt(defaultPromptWithReferencePair);
        }
    }
 
    const handleConfirmStartImageGeneration = async () => {
        try {
            setIsLoading(true);

            // Validate that the selected reference image exists in S3
            if (oemTaPair && oemTaPair !== NO_REFERENCE) {
                try {
                    await Storage.get(`processed_images/${oemTaPair}.ai-OEM.png`, {
                        bucket: aws_config.backend.bucket_name,
                        region: aws_config.backend.region,
                        level: "public",
                        customPrefix: { public: "" },
                        validateObjectExistence: true,
                    });
                } catch {
                    ToastError(t("ImageGeneration.ReferenceImageNotAvailable"));
                    setIsLoading(false);
                    return;
                }
            }

            const response = await startImageGenerationProcess(imageId, oemTaPair === NO_REFERENCE ? "" : oemTaPair, prompt, imageGenModel);
            if (response) {
                toast.success(
                    <span>{t("ImageGeneration.SuccessMessage").replace("{link}", "")}
                        <a href="/image-generation">{t("ImageGeneration.LinkText")}</a></span>,
                    {
                        closeOnClick: false,
                    }
                );
            }
        } catch (error) {
            ToastError(error as string);
        } finally {
            setIsLoading(false);
            setIsShowConfirmationModal(false);
        }
    };
 
    const renderPairOptions = () => {
        return availableOemTaPairs.length === 0 ?
            <option value="">{t("ImageGeneration.PlaceholderNoReferenceImage")}</option> :
            availableOemTaPairs.map((pair: string) => (
                <option key={pair} value={pair}>
                    {pair === NO_REFERENCE ? t("ImageGeneration.PlaceholderNoReferenceImage") : pair}
                </option>
            ))
    }
 
    return (
        hasImageGenerationPermission && (
        <div className="d-flex justify-content-end">
            <div className="d-flex flex-row gap-2 align-items-center">
                <Button
                    variant="secondary"
                    onClick={() => setIsShowConfirmationModal(true)}
                    style={{ marginBottom: "10px" }}>
                    {t("ImageGeneration.Start")}
                </Button>
            </div>

            <ConfirmationModal
                show={isShowConfirmationModal}
                isLoading={isLoading}
                title={t("ImageGeneration.ConfirmationTitle")}
                body={
                    <Form>
                        <Form.Group controlId="promptInput">
                            <Form.Label>{t("ImageGeneration.PromptLabel")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={10}
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder={t("ImageGeneration.PromptPlaceholder")}
                                style={{ resize: "vertical", overflowX: "hidden" }}
                            />
                        </Form.Group>
                        <Form.Group controlId="oemTaPairSelect">
                            <Form.Label>{t("ImageGeneration.OemTaPairLabel")}</Form.Label>
                            <Form.Control
                                as="select"
                                value={oemTaPair}
                                disabled={availableOemTaPairs.length === 0}
                                onChange={(e) => handleOemTaPairChange(e.target.value)}
                                placeholder={t("ImageGeneration.OemTaPairPlaceholder")}
                            >
                                {renderPairOptions()}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group controlId="imageGenModelSelect">
                            <Form.Label>{t("ImageGeneration.ModelLabel")}</Form.Label>
                            <Form.Control
                                as="select"
                                value={imageGenModel}
                                onChange={(e) => setImageGenModel(e.target.value)}
                            >
                                {availableModels.map((model) => (
                                    <option key={model.value} value={model.value}>
                                        {model.label}
                                    </option>
                                ))}
                            </Form.Control>
                            {(() => {
                                const selected = availableModels.find(m => m.value === imageGenModel);
                                return selected ? (
                                    <div style={{ marginTop: "8px", padding: "8px 10px", background: "#f8f9fa", borderRadius: "6px", fontSize: "0.82rem", color: "#444", border: "1px solid #dee2e6" }}>
                                        <div style={{ marginBottom: "4px" }}>{selected.description}</div>
                                        <div style={{ display: "flex", gap: "16px", marginTop: "4px", flexWrap: "wrap" }}>
                                            <span>💰 <strong>Cost:</strong> {selected.cost}</span>
                                            <span>⚡ <strong>Speed:</strong> {selected.speed}</span>
                                            <span>🎨 <strong>Quality:</strong> {selected.quality}</span>
                                        </div>
                                    </div>
                                ) : null;
                            })()}
                        </Form.Group>
                        
                    </Form>
                }
                onConfirm={handleConfirmStartImageGeneration}
                onCancel={() => setIsShowConfirmationModal(false)}
            />
        </div>
        )
    )
}

export default StartImageGeneration